import { useCallback } from 'react';
import type { UpdateProjectAggThrottleContext } from '@scale/genai-compass-shared/cards/update_project_agg_throttle';
import { useCompassCard } from '@scale/compass-ui/store';
import { columnsFromRecord } from '@scale/compass-shared/utils';

const useUpdateProjectAggThrottleCard = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const context = card.defaultContext as UpdateProjectAggThrottleContext;

  const availableColumns = columnsFromRecord(retrievedOutput?.response?.data) || [];

  const validateAndMarkCanSubmit = useCallback(
    (updatedContext: UpdateProjectAggThrottleContext) => {
      const canSubmit =
        Boolean(updatedContext.columnIdWithProjectId) &&
        Boolean(updatedContext.columnIdWithThrottleEntryId) &&
        Boolean(updatedContext.columnIdWithThreshold);
      updateCardCanSubmit(canSubmit);
    },
    [updateCardCanSubmit],
  );

  const setColumnIdWithProjectId = useCallback(
    (columnIdWithProjectId: string) => {
      const updatedContext: UpdateProjectAggThrottleContext = { ...context, columnIdWithProjectId };
      updateCardContext(updatedContext);
      validateAndMarkCanSubmit(updatedContext);
    },
    [context, updateCardContext, validateAndMarkCanSubmit],
  );

  const setColumnIdWithThrottleEntryId = useCallback(
    (columnIdWithThrottleEntryId: string) => {
      const updatedContext: UpdateProjectAggThrottleContext = {
        ...context,
        columnIdWithThrottleEntryId,
      };
      updateCardContext(updatedContext);
      validateAndMarkCanSubmit(updatedContext);
    },
    [context, updateCardContext, validateAndMarkCanSubmit],
  );

  const setColumnIdWithThreshold = useCallback(
    (columnIdWithThreshold: string) => {
      const updatedContext: UpdateProjectAggThrottleContext = { ...context, columnIdWithThreshold };
      updateCardContext(updatedContext);
      validateAndMarkCanSubmit(updatedContext);
    },
    [context, updateCardContext, validateAndMarkCanSubmit],
  );

  const setDryRun = useCallback(
    (dryRun: boolean) => {
      const updatedContext: UpdateProjectAggThrottleContext = { ...context, dryRun };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  return {
    columnIdWithProjectId: context.columnIdWithProjectId,
    columnIdWithThrottleEntryId: context.columnIdWithThrottleEntryId,
    columnIdWithThreshold: context.columnIdWithThreshold,
    dryRun: context.dryRun ?? false,
    setColumnIdWithProjectId,
    setColumnIdWithThrottleEntryId,
    setColumnIdWithThreshold,
    setDryRun,
    availableColumns,
  };
};

export default useUpdateProjectAggThrottleCard;
